import React from 'react';
import { MdOutlineCheckCircle, MdOutlineWarningAmber, MdOutlineRemove, MdOutlineErrorOutline } from 'react-icons/md';
import { STATUS_LABELS, statusTone, fmtDuration, safeArray, isNum } from '../format';

// `nearby_places` is the deprecated umbrella that property-context 2.2.0 split into
// commercial_anchors / public_places / adverse_uses. It stays in the response, so it
// keeps a row here — muted whenever any of its successors is present.
export const DATASETS = [
  { key: 'mls_property_details', label: 'Listing', anchor: 'le-section-property' },
  { key: 'zoning', label: 'Zoning', anchor: 'le-section-property' },
  { key: 'parcel_terrain', label: 'Terrain', anchor: 'le-section-property' },
  { key: 'site_screening', label: 'Site screening', anchor: 'le-section-screening' },
  { key: 'tree_canopy', label: 'Tree canopy', anchor: 'le-section-canopy' },
  { key: 'building_footprints', label: 'Building footprints', anchor: 'le-section-footprints' },
  { key: 'county_deed_history', label: 'Deeds & history', anchor: 'le-section-deeds' },
  { key: 'sale_comps', label: 'Sale comps', anchor: 'le-section-comps' },
  { key: 'schools', label: 'Schools', anchor: 'le-section-schools' },
  { key: 'commercial_anchors', label: 'Commercial anchors', anchor: 'le-section-places', supersedes: 'nearby_places' },
  { key: 'public_places', label: 'Public & recreational places', anchor: 'le-section-places', supersedes: 'nearby_places' },
  { key: 'adverse_uses', label: 'Adverse uses', anchor: 'le-section-places', supersedes: 'nearby_places' },
  { key: 'nearby_places', label: 'Nearby places', anchor: 'le-section-places', legacy: true },
  { key: 'demographics', label: 'Demographics', anchor: 'le-section-demographics' },
  { key: 'housing_market', label: 'Housing market', anchor: 'le-section-housing' },
  { key: 'county_economy', label: 'County economy', anchor: 'le-section-economy' },
  { key: 'employment_access', label: 'Employment', anchor: 'le-section-employment' },
];

/** What the tool advertises before a result exists (the deprecated umbrella is not promised). */
export const PRIMARY_DATASETS = DATASETS.filter((d) => !d.legacy);

/**
 * Datasets present in a `section_status`, in display order, each flagged
 * `deprecated` when a dataset that supersedes it is also present.
 */
export function visibleDatasets(sectionStatus) {
  if (!sectionStatus) return [];
  const superseded = new Set(DATASETS.filter((d) => d.supersedes && sectionStatus[d.key]).map((d) => d.supersedes));
  return DATASETS.filter((d) => sectionStatus[d.key]).map((d) => ({ ...d, deprecated: superseded.has(d.key) }));
}

function iconFor(tone) {
  if (tone === 'good') return <MdOutlineCheckCircle size={14} />;
  if (tone === 'warning') return <MdOutlineWarningAmber size={14} />;
  if (tone === 'critical') return <MdOutlineErrorOutline size={14} />;
  return <MdOutlineRemove size={14} />;
}

export default function SectionStatusStrip({ sectionStatus, timing, elapsedMs }) {
  if (!sectionStatus) return null;
  const visible = visibleDatasets(sectionStatus);
  const durations = visible.filter((d) => !d.deprecated).map((d) => ({ label: d.label, ms: sectionStatus[d.key]?.duration_ms })).filter((d) => isNum(d.ms));
  const slowest = durations.reduce((best, d) => (best === null || d.ms > best.ms ? d : best), null);
  const sum = durations.reduce((acc, d) => acc + d.ms, 0);
  const serverTotal = isNum(timing?.total_ms) ? timing.total_ms : null;
  const jump = (anchor) => {
    const el = document.getElementById(anchor);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const successorsOf = (key) => DATASETS.filter((d) => d.supersedes === key && sectionStatus[d.key]).map((d) => d.label).join(', ');
  return (
    <div className="le-status-block">
    <div className="le-status-strip" aria-label="Dataset status">
      {visible.map((d) => {
        const st = sectionStatus[d.key];
        const tone = statusTone(st?.status);
        const warnings = safeArray(st?.warnings);
        const title = [
          d.deprecated ? `Deprecated umbrella — superseded by ${successorsOf(d.key)}` : null,
          `${STATUS_LABELS[st?.status] || st?.status || 'unknown'} · ${fmtDuration(st?.duration_ms)}`,
          ...warnings,
        ].filter(Boolean).join('\n');
        return (
          <button type="button" key={d.key} className={`le-status-chip le-status-${tone} ${d.deprecated ? 'le-status-deprecated' : ''}`} onClick={() => jump(d.anchor)} title={title}>
            {iconFor(tone)}
            <span>{d.label}</span>
            {d.deprecated ? <span className="le-status-chip-state">deprecated</span> : null}
            {st?.status && st.status !== 'ok' ? <span className="le-status-chip-state">{STATUS_LABELS[st.status] || st.status}</span> : null}
            {st?.cached ? <span className="le-status-chip-time" title="Served from cache">cached</span> : isNum(st?.duration_ms) ? <span className="le-status-chip-time">{fmtDuration(st.duration_ms)}</span> : null}
          </button>
        );
      })}
    </div>
    {durations.length ? (
      <div className="le-timing-line" title={timing?.concurrency || undefined}>
        {serverTotal !== null ? <span>Server {fmtDuration(serverTotal)}</span> : null}
        {isNum(elapsedMs) ? <span>Round trip {fmtDuration(elapsedMs)}</span> : null}
        {slowest ? <span>Slowest {slowest.label} {fmtDuration(slowest.ms)}</span> : null}
        <span>Datasets sum to {fmtDuration(sum)} but ran concurrently{isNum(timing?.location_resolution_ms) ? ` after a ${fmtDuration(timing.location_resolution_ms)} location resolve` : ''}</span>
      </div>
    ) : null}
    </div>
  );
}
